import { create } from 'zustand'
import { organizationService } from '@/services/organization'
import type { Organization, Project } from '@/schemas/organization'

interface PersistedContext {
  organizationId: string | null
  projectId: string | null
}

interface OrganizationState {
  organizations: Organization[]
  currentOrganization: Organization | null
  projects: Project[]
  currentProject: Project | null
  isLoading: boolean
  isInitialized: boolean

  // Actions
  fetchOrganizations: () => Promise<void>
  fetchProjects: (orgId: string) => Promise<void>
  setCurrentOrganization: (org: Organization) => void
  setCurrentProject: (project: Project | null) => void
  addOrganization: (org: Organization) => void
  addProject: (project: Project) => void
  clearContext: () => void
  initializeContext: () => Promise<void>
}

// Separate storage for persisted context
const STORAGE_KEY = 'archon-org-context'

const getPersistedContext = (): PersistedContext => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Ignore parse errors
  }
  return { organizationId: null, projectId: null }
}

const setPersistedContext = (context: PersistedContext) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(context))
}

const clearPersistedContext = () => {
  localStorage.removeItem(STORAGE_KEY)
}

export const useOrganizationStore = create<OrganizationState>()((set, get) => ({
  organizations: [],
  currentOrganization: null,
  projects: [],
  currentProject: null,
  isLoading: false,
  isInitialized: false,

  fetchOrganizations: async () => {
    set({ isLoading: true })
    try {
      const response = await organizationService.getOrganizations()
      set({ organizations: response.organizations })
    } catch (error) {
      console.error('Failed to fetch organizations:', error)
      // Keep existing data on error instead of clearing
    } finally {
      set({ isLoading: false })
    }
  },

  fetchProjects: async (orgId: string) => {
    set({ isLoading: true })
    try {
      const response = await organizationService.getProjects(orgId)
      set({ projects: response.projects })
    } catch (error) {
      console.error('Failed to fetch projects:', error)
      // Keep existing data on error instead of clearing
    } finally {
      set({ isLoading: false })
    }
  },

  setCurrentOrganization: (org: Organization) => {
    set({
      currentOrganization: org,
      projects: [],
      currentProject: null,
    })
    setPersistedContext({
      organizationId: org.id,
      projectId: null,
    })
    // Fetch projects for the new organization
    get().fetchProjects(org.id)
  },

  setCurrentProject: (project: Project | null) => {
    const { currentOrganization } = get()
    set({ currentProject: project })
    setPersistedContext({
      organizationId: currentOrganization?.id ?? null,
      projectId: project?.id ?? null,
    })
  },

  addOrganization: (org: Organization) => {
    set((state) => ({ organizations: [...state.organizations, org] }))
  },

  addProject: (project: Project) => {
    set((state) => ({ projects: [...state.projects, project] }))
  },

  clearContext: () => {
    set({
      organizations: [],
      currentOrganization: null,
      projects: [],
      currentProject: null,
      isInitialized: false,
    })
    clearPersistedContext()
  },

  initializeContext: async () => {
    const { fetchOrganizations, fetchProjects, isInitialized } = get()

    // Avoid re-initialization
    if (isInitialized) return

    set({ isLoading: true })

    try {
      // Fetch organizations first
      await fetchOrganizations()

      const { organizations } = get()
      if (organizations.length === 0) {
        set({ isInitialized: true, isLoading: false })
        return
      }

      // Try to restore persisted context
      const persisted = getPersistedContext()
      let targetOrg: Organization | null = null
      let targetProject: Project | null = null

      if (persisted.organizationId) {
        targetOrg = organizations.find((o) => o.id === persisted.organizationId) ?? null
      }

      // If no valid persisted org, use the first one
      if (!targetOrg) {
        targetOrg = organizations[0]
      }

      set({ currentOrganization: targetOrg })

      // Fetch projects for the current organization
      if (targetOrg) {
        await fetchProjects(targetOrg.id)

        const { projects } = get()

        // Try to restore persisted project
        if (persisted.projectId) {
          targetProject = projects.find((p) => p.id === persisted.projectId) ?? null
        }

        set({ currentProject: targetProject })

        // Update persisted context with validated values
        setPersistedContext({
          organizationId: targetOrg.id,
          projectId: targetProject?.id ?? null,
        })
      }
    } catch (error) {
      console.error('Failed to initialize context:', error)
    } finally {
      set({ isInitialized: true, isLoading: false })
    }
  },
}))
